import type { ComponentType, ReactNode, SVGProps } from "react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AUDIT_LOG_ICON,
  DETAILS_ICON,
  INFO_ICON,
  RAW_DATA_LOG_ICON,
  REFUELING_ICON_COMPONENT,
  ROUTES_ICON,
  TACHOGRAPH_ICON,
  TPMS_ICON,
} from "src/assets/svgs";
import {
  CustomActionButtons,
  CustomModal,
  CustomTabs,
  CustomTypography,
} from "sipli-fleet-core/components";
import { useQueryGetVehicleById } from "src/queries/unitManagement.queries";
import "../../Customers.css";
import CustomerBreadcrumb from "../CustomerBreadcrumb";
import DeviceHomeTab from "./DeviceHomeTab";
import type { CustomerDevice } from "./DevicesTab";
import RoutesHistoryTab from "./RoutesHistoryTab";
import { renderTabLabel } from "../../constant";
import DeviceDetailsTab from "./DetailsTab/DeviceDetailsTab";
import AuditLogTab from "./AuditLogTab";
import TachographSettings from "./TachographSettings";
import RawDataLog from "../../RawDataLog";
import { NAVIGATION_GUARD_EVENT, type NavigationGuardDetail } from "src/util/navigationGuard";

const FuelLevelHistoryTab = lazy(() => import("./FuelLevelHistoryTab"));
const TpmsTab = lazy(() => import("./TpmsTab"));

type DetailRecord = Record<string, unknown>;
type TabKey =
  | "info"
  | "details"
  | "raw_data_log"
  | "routes"
  | "refueling"
  | "tachograph"
  | "audit_log"
  | "tpms";
type SvgIcon = ComponentType<SVGProps<SVGSVGElement>>;

const InfoIcon = INFO_ICON as unknown as SvgIcon;
const DetailsIcon = DETAILS_ICON as unknown as SvgIcon;
const RawDataLogIcon = RAW_DATA_LOG_ICON as unknown as SvgIcon;
const RoutesIcon = ROUTES_ICON as unknown as SvgIcon;
const RefuelingIcon = REFUELING_ICON_COMPONENT as unknown as SvgIcon;
const TachographIcon = TACHOGRAPH_ICON as unknown as SvgIcon;
const AuditLogIcon = AUDIT_LOG_ICON as unknown as SvgIcon;
const TpmsIcon = TPMS_ICON as unknown as SvgIcon;

const tabKeys: TabKey[] = [
  "info",
  "details",
  "raw_data_log",
  "routes",
  "refueling",
  "tachograph",
  "audit_log",
  "tpms",
];

const asRecord = (value: unknown): DetailRecord =>
  value && typeof value === "object" ? (value as DetailRecord) : {};

const readFirstValue = (records: DetailRecord[], keys: string[]): unknown => {
  for (const record of records) {
    for (const key of keys) {
      const value = record[key];

      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
  }

  return undefined;
};

const displayValue = (value: unknown): ReactNode => {
  if (value === undefined || value === null || value === "") return "--";
  if (typeof value === "boolean") return value ? "ON" : "OFF";
  if (typeof value === "string" || typeof value === "number") return value;

  const record = asRecord(value);
  return String(readFirstValue([record], ["name", "label", "title", "value", "code"]) ?? "--");
};

const displayText = (value: unknown) => String(displayValue(value));

const toTitleText = (value: unknown): string => {
  if (value === undefined || value === null || value === "" || value === "--") return "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();

  const record = asRecord(value);
  return toTitleText(record.name ?? record.label ?? record.title ?? record.value ?? record.code);
};

const pickText = (values: unknown[]): string => {
  for (const value of values) {
    const text = toTitleText(value);

    if (text) return text;
  }

  return "";
};

const pickIdentifier = (values: unknown[]): string | number | undefined => {
  for (const value of values) {
    if (typeof value === "string" || typeof value === "number") return value;
  }

  return undefined;
};

const CustomerDeviceDetail = () => {
  const { clientId, unitId, vehicleId } = useParams();
  const { state, search, pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tpmsDirty, setTpmsDirty] = useState(false);
  const [unsavedChangesOpen, setUnsavedChangesOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const stateDevice = (state as { device?: CustomerDevice } | null)?.device;

  const { data: vehicleDetail, isLoading: isVehicleDetailLoading } = useQueryGetVehicleById(
    vehicleId,
    {
      enabled: !!vehicleId,
    }
  );
  const vehicleDetailRecord = asRecord(vehicleDetail);
  const vehicleRecord = asRecord(vehicleDetailRecord.vehicle ?? vehicleDetailRecord.vehicleDetails);
  const deviceDetailRecord = asRecord(vehicleDetailRecord.device);
  const unitDetailRecord = asRecord(deviceDetailRecord.unit ?? vehicleDetailRecord.unit);
  const modelDetailRecord = asRecord(
    vehicleRecord.model ?? vehicleDetailRecord.model ?? unitDetailRecord.model
  );
  const deviceModel = asRecord(unitDetailRecord.model);
  const modelId = pickIdentifier([deviceModel.id]);
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const requestedTab = queryParams.get("tab");
  const activeTab: TabKey = tabKeys.includes(requestedTab as TabKey)
    ? (requestedTab as TabKey)
    : "info";

  const stateVehicleName = displayText(stateDevice?.vehicle);
  const apiVehicleName = displayText(
    readFirstValue([asRecord(vehicleDetail)], ["vehicleName", "name", "label", "model"])
  );
  const breadcrumbVehicleName =
    stateVehicleName !== "--"
      ? stateVehicleName
      : apiVehicleName !== "--"
        ? apiVehicleName
        : `${t("vehicle")} ${vehicleId ?? ""}`.trim();

  const [breadcrumbModelName = "", ...breadcrumbBrandParts] = breadcrumbVehicleName.split(/\s+/);
  const brandName = pickText([
    vehicleRecord.brand,
    vehicleRecord.make,
    vehicleRecord.vehicleMake,
    vehicleRecord.brandName,
    vehicleRecord.makeName,
    vehicleDetailRecord.brand,
    vehicleDetailRecord.make,
    vehicleDetailRecord.vehicleMake,
    vehicleDetailRecord.brandName,
    vehicleDetailRecord.makeName,
    modelDetailRecord.make,
    modelDetailRecord.brand,
    breadcrumbBrandParts.join(" "),
  ]);
  const modelName = pickText([
    modelDetailRecord.name,
    modelDetailRecord.label,
    modelDetailRecord.title,
    vehicleDetailRecord.modelName,
    vehicleDetailRecord.vehicleModelName,
    unitDetailRecord.modelName,
    deviceDetailRecord.modelName,
    breadcrumbModelName,
  ]);
  const serial = pickText([
    unitDetailRecord.serial,
    deviceDetailRecord.serial,
    vehicleDetailRecord.serial,
  ]);
  const imei = pickText([unitDetailRecord.imei, deviceDetailRecord.imei, vehicleDetailRecord.imei]);
  const unitMakeRecord = asRecord(
    unitDetailRecord.make ?? unitDetailRecord.brand ?? modelDetailRecord.make
  );
  const rawDataBrandName = pickText([
    unitMakeRecord.name,
    unitDetailRecord.makeName,
    unitDetailRecord.brandName,
    deviceDetailRecord.makeName,
  ]);
  const rawDataMakeId = pickIdentifier([
    unitMakeRecord.id,
    unitDetailRecord.makeId,
    deviceDetailRecord.makeId,
  ]);
  const licensePlate = pickText([
    vehicleRecord.licensePlate,
    vehicleRecord.licencePlate,
    vehicleDetailRecord.licensePlate,
    vehicleDetailRecord.licencePlate,
  ]);
  const vin = pickText([
    vehicleRecord.vin,
    vehicleRecord.VIN,
    vehicleDetailRecord.vin,
    vehicleDetailRecord.VIN,
  ]);
  const vehicleTitle =
    vehicleDetail && !isVehicleDetailLoading
      ? [brandName, modelName, serial, licensePlate || vin].filter(Boolean).join(" ")
      : "";

  const commitTabChange = useCallback(
    (key: string) => {
      const nextTab = tabKeys.includes(key as TabKey) ? key : "info";
      const params = new URLSearchParams(search);
      params.set("tab", nextTab);

      navigate(`${pathname}?${params.toString()}`, {
        replace: true,
        state,
      });
    },
    [navigate, pathname, search, state]
  );

  const requestNavigation = useCallback(
    (navigation: () => void) => {
      if (activeTab !== "tpms" || !tpmsDirty) {
        navigation();
        return;
      }

      setPendingNavigation(() => navigation);
      setUnsavedChangesOpen(true);
    },
    [activeTab, tpmsDirty]
  );

  const handleTabChange = (key: string) => requestNavigation(() => commitTabChange(key));

  useEffect(() => {
    if (activeTab !== "tpms" || !tpmsDirty) return;
    let restoringHistory = false;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const handleLinkClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target as Element | null;
      const anchor = target?.closest<HTMLAnchorElement>("a[href]");
      if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download")) return;
      if (anchor.href === window.location.href) return;

      event.preventDefault();
      event.stopPropagation();
      requestNavigation(() => window.location.assign(anchor.href));
    };
    const handleGuardedNavigation = (event: Event) => {
      const navigationEvent = event as CustomEvent<NavigationGuardDetail>;
      event.preventDefault();
      requestNavigation(navigationEvent.detail.proceed);
    };
    const handlePopState = () => {
      if (restoringHistory) {
        restoringHistory = false;
        return;
      }

      const destination = window.location.href;
      restoringHistory = true;
      window.history.forward();
      window.setTimeout(() => requestNavigation(() => window.location.assign(destination)), 0);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    document.addEventListener("click", handleLinkClick, true);
    window.addEventListener(NAVIGATION_GUARD_EVENT, handleGuardedNavigation);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("click", handleLinkClick, true);
      window.removeEventListener(NAVIGATION_GUARD_EVENT, handleGuardedNavigation);
    };
  }, [activeTab, requestNavigation, tpmsDirty]);

  const handleStay = () => {
    setUnsavedChangesOpen(false);
    setPendingNavigation(null);
  };

  const handleLeaveWithoutSaving = () => {
    const navigation = pendingNavigation;
    setTpmsDirty(false);
    setUnsavedChangesOpen(false);
    setPendingNavigation(null);
    window.setTimeout(() => navigation?.(), 0);
  };

  return (
    <section className="min-h-full py-6 bg-gray1">
      <div className="customer-detail-header">
        <CustomTypography.CustomTitle
          level={3}
          className="customer-detail-title !text-[25.23px] !m-0 !font-bold"
        >
          {vehicleTitle || "--"}
        </CustomTypography.CustomTitle>
        <div className="min-w-0 col-start-2">
          <CustomerBreadcrumb
            ariaLabel={t("vehicle_details")}
            separator="/"
            items={[
              {
                label: t("customers"),
                path: "/customers",
              },
              {
                label: t("vehicle_list"),
                path: `/customers/${encodeURIComponent(clientId ?? "")}?tab=devices`,
                state,
              },
              {
                label: breadcrumbVehicleName,
              },
            ]}
          />
        </div>
      </div>

      <CustomTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        className="customer-detail-tabs"
        items={[
          {
            key: "info",
            label: renderTabLabel(InfoIcon, t("info"), activeTab === "info"),
            children: <DeviceHomeTab unitId={unitId} vehicleId={vehicleId} modelId={modelId} />,
          },
          {
            key: "details",
            label: renderTabLabel(DetailsIcon, t("details"), activeTab === "details"),
            children: <DeviceDetailsTab unitId={unitId} />,
          },
          {
            key: "raw_data_log",
            label: renderTabLabel(RawDataLogIcon, t("raw_data_log"), activeTab === "raw_data_log"),
            children: (
              <RawDataLog
                embedded
                initialImei={imei}
                initialSerial={serial}
                initialBrandName={rawDataBrandName}
                initialMakeId={rawDataMakeId}
              />
            ),
          },
          {
            key: "routes",
            label: renderTabLabel(RoutesIcon, t("routes"), activeTab === "routes"),
            children: <RoutesHistoryTab vehicleId={vehicleId} unitId={unitId} />,
          },
          {
            key: "refueling",
            label: renderTabLabel(RefuelingIcon, t("refueling"), activeTab === "refueling"),
            children: (
              <Suspense fallback={<div className="min-h-[240px]" />}>
                <FuelLevelHistoryTab unitId={unitId} vehicleId={vehicleId} />
              </Suspense>
            ),
          },
          {
            key: "tachograph",
            label: renderTabLabel(TachographIcon, t("tachograph"), activeTab === "tachograph"),
            children: <TachographSettings vehicleId={vehicleId} />,
          },
          {
            key: "audit_log",
            label: renderTabLabel(AuditLogIcon, t("audit_log"), activeTab === "audit_log"),
            children: <AuditLogTab vehicleId={vehicleId} />,
          },
          {
            key: "tpms",
            label: renderTabLabel(TpmsIcon, t("tpms"), activeTab === "tpms"),
            children: (
              <Suspense fallback={<div className="min-h-[330px]" />}>
                <TpmsTab
                  vehicleId={vehicleId}
                  onDirtyChange={setTpmsDirty}
                  onNavigationAttempt={requestNavigation}
                />
              </Suspense>
            ),
          },
        ]}
      />
      <CustomModal
        open={unsavedChangesOpen}
        onCancel={handleStay}
        showFooter={false}
        width={473}
        closable={false}
        maskClosable={true}
        keyboard={true}
        className="unsaved-changes-modal"
        centered
        destroyOnHidden
      >
        <div className="flex flex-col items-center justify-between h-full">
          <div className="flex flex-col items-center justify-center gap-[8px]">
            <CustomTypography.CustomText className="!text-[19.93px] !font-bold !leading-[28px]">
              {t("unsaved_changes")}
            </CustomTypography.CustomText>
            <CustomTypography.CustomText className="!leading-[18px] text-center !text-gray10">
              {t("unsaved_changes_description")}
            </CustomTypography.CustomText>
          </div>
          <CustomActionButtons
            cancelBtnLabel={t("leave_without_saving")}
            okBtnLabel={t("stay")}
            onCancel={handleLeaveWithoutSaving}
            onOk={handleStay}
            className="h-[32px] w-full"
            cancelProps={{
              className: "h-full w-[50%] leading-[18px]",
            }}
            okProps={{
              className: "h-full w-[50%] leading-[18px] !text-gray1",
            }}
          />
        </div>
      </CustomModal>
    </section>
  );
};

export default CustomerDeviceDetail;
